import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { BaseEdge, EdgeLabelRenderer, useReactFlow, Position } from 'reactflow';
import { useDispatch, useSelector } from 'react-redux';
import { updateEdgeData } from '../../../../redux/reducers/global/edge/edgeSlice';
import { computeCustomHandleAnchors } from './customHandleAnchors';
import { getBusLayout, getBusHandleAnchor } from '../../../../utilities/busLayout';

function useGridSnap() {
  const gridGap = useSelector((state) => state.view.gridGapPx) || 25;
  const gap = Math.max(5, Math.min(200, Number(gridGap) || 25));
  const snap = (v) => Math.round(v / gap) * gap;
  return snap;
}

function mergeCollinear(points) {
  if (!points || points.length <= 2) return points || [];
  const out = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const a = out[out.length - 1];
    const b = points[i];
    const c = points[i + 1];
    const collinearX = a.x === b.x && b.x === c.x;
    const collinearY = a.y === b.y && b.y === c.y;
    if (collinearX || collinearY) {
      continue; // drop b
    }
    out.push(b);
  }
  out.push(points[points.length - 1]);
  return out;
}

function isCollinearPoint(prev, point, next) {
  if (!prev || !point || !next) return false;
  return (prev.x === point.x && point.x === next.x)
    || (prev.y === point.y && point.y === next.y);
}

function isStraightishPolyline(points, tolerance = 8) {
  if (!Array.isArray(points) || points.length < 2) return false;
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return false;
  const vertical = Math.abs(first.x - last.x) <= tolerance;
  const horizontal = Math.abs(first.y - last.y) <= tolerance;
  if (vertical && points.every((pt) => pt && Math.abs(pt.x - first.x) <= tolerance)) {
    return true;
  }
  if (horizontal && points.every((pt) => pt && Math.abs(pt.y - first.y) <= tolerance)) {
    return true;
  }
  return false;
}

function straightenPolyline(points, tolerance = 8) {
  if (!isStraightishPolyline(points, tolerance)) return points || [];
  const first = points[0];
  const last = points[points.length - 1];
  if (Math.abs(first.x - last.x) <= tolerance) {
    const x = Math.round(((first.x + last.x) / 2) * 100) / 100;
    return [{ x, y: first.y }, { x, y: last.y }];
  }
  const y = Math.round(((first.y + last.y) / 2) * 100) / 100;
  return [{ x: first.x, y }, { x: last.x, y }];
}

function clampValue(value, min, max) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return min;
  return Math.min(max, Math.max(min, numeric));
}

function buildPoints(sourceX, sourceY, targetX, targetY, waypoints) {
  const pts = [{ x: sourceX, y: sourceY }, ...(waypoints || []), { x: targetX, y: targetY }];
  if (pts.length === 2) {
    // default L-shaped
    const mid = { x: sourceX, y: targetY };
    return mergeCollinear([pts[0], mid, pts[1]]);
  }
  // ensure all segments are orthogonal
  return mergeCollinear((() => {
    const out = [pts[0]];
    for (let i = 1; i < pts.length; i++) {
      const a = out[out.length - 1];
      const b = pts[i];
      if (a.x !== b.x && a.y !== b.y) {
        out.push({ x: a.x, y: b.y });
      }
      out.push(b);
    }
    return out;
  })());
}

function toPath(points) {
  if (!points || points.length === 0) return '';
  const [first, ...rest] = points;
  const cmds = [`M ${first.x} ${first.y}`];
  rest.forEach((p) => cmds.push(`L ${p.x} ${p.y}`));
  return cmds.join(' ');
}

const ENDPOINT_OVERLAP_PX = 2;
const ENDPOINT_OVERLAP_ROTATED_PX = 1.28;

function extendEndpointOverlap(
  points,
  sourceOverlapPx = ENDPOINT_OVERLAP_PX,
  targetOverlapPx = ENDPOINT_OVERLAP_PX,
) {
  if (!Array.isArray(points) || points.length < 2) return points || [];
  const out = points.map((pt) => ({ ...pt }));
  const first = out[0];
  const second = out[1];
  const lastIndex = out.length - 1;
  const last = out[lastIndex];
  const penultimate = out[lastIndex - 1];

  if (first && second && sourceOverlapPx > 0) {
    if (first.x === second.x && first.y !== second.y) {
      first.y += first.y < second.y ? -sourceOverlapPx : sourceOverlapPx;
    } else if (first.y === second.y && first.x !== second.x) {
      first.x += first.x < second.x ? -sourceOverlapPx : sourceOverlapPx;
    }
  }

  if (last && penultimate && targetOverlapPx > 0) {
    if (last.x === penultimate.x && last.y !== penultimate.y) {
      last.y += last.y > penultimate.y ? targetOverlapPx : -targetOverlapPx;
    } else if (last.y === penultimate.y && last.x !== penultimate.x) {
      last.x += last.x > penultimate.x ? targetOverlapPx : -targetOverlapPx;
    }
  }

  return out;
}

const getEndpointOverlapPx = (angle) => (
  angle === 0 ? ENDPOINT_OVERLAP_PX : ENDPOINT_OVERLAP_ROTATED_PX
);

function normalizeEndpointOffsets(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const out = points.map((pt) => ({ ...pt }));
  if (out.length < 3) {
    return out;
  }
  const start = out[0];
  const first = out[1];
  if (start && first && start.x !== first.x && start.y !== first.y) {
    out.splice(1, 0, { x: first.x, y: start.y });
  }
  const last = out[out.length - 1];
  const penultimateIndex = out.length - 2;
  const penultimate = out[penultimateIndex];
  if (penultimate && last && penultimate.x !== last.x && penultimate.y !== last.y) {
    out.splice(out.length - 1, 0, { x: last.x, y: penultimate.y });
  }
  return out;
}

const MoveHandleIcon = ({ direction }) => {
  if (direction === 'horizontal') {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M6 5l-4 4 4 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 13l4-4-4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="5.5" y1="9.5" x2="14.5" y2="9.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M5 6l4-4 4 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 14l-4 4-4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="9.5" y1="5.5" x2="9.5" y2="14.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
};

// Build attribute UI similar to nodes: show visible attributes in rows


const OrthogonalEdge = (props) => {
  const {
    id,
    source,
    target,
    // sourceHandle, targetHandle are not guaranteed on edge renderer props
    sourceX,
    sourceY,
    targetX,
    targetY,
    markerStart,
    markerEnd,
    data = {},
    selected,
  } = props;
  

  const dispatch = useDispatch();
  const reactFlow = useReactFlow();
  const gridGapPx = useSelector((state) => state.view.gridGapPx) || 25;
  const snap = useGridSnap();
  // Subscribe to source/target node angle to force edge rerender on rotation
  const srcAngle = useSelector((state) => {
    const n = (state.node.nodes || []).find((x) => x.id === source);
    return typeof n?.data?.angle === 'number' ? n.data.angle : 0;
  });
  const tgtAngle = useSelector((state) => {
    const n = (state.node.nodes || []).find((x) => x.id === target);
    return typeof n?.data?.angle === 'number' ? n.data.angle : 0;
  });
  const [dragIndex, setDragIndex] = useState(-1);
  const [localPoints, setLocalPoints] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(-1);
  const containerRef = useRef(null);
  const [hoverPath, setHoverPath] = useState(false);
  const [segmentDragging, setSegmentDragging] = useState(false);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(null);
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState(null);
  const segmentDragRef = useRef(null);
  const computeSegmentRange = useCallback((segIdx, isVerticalSegment, pts) => {
    if (!Array.isArray(pts) || pts.length < 2) {
      return { startIndex: 1, endIndex: pts.length - 2 };
    }
    let start = segIdx;
    let end = segIdx + 1;
    if (isVerticalSegment) {
      const xVal = pts[segIdx].x;
      while (start > 1 && pts[start - 1].x === xVal) start--;
      while (end < pts.length - 2 && pts[end + 1].x === xVal) end++;
    } else {
      const yVal = pts[segIdx].y;
      while (start > 1 && pts[start - 1].y === yVal) start--;
      while (end < pts.length - 2 && pts[end + 1].y === yVal) end++;
    }
    if ((segIdx === 0 || segIdx === pts.length - 2) && pts.length > 2) {
      start = 1;
      end = Math.max(1, pts.length - 2);
    }
    start = Math.max(start, 1);
    end = Math.min(end, pts.length - 2);
    return { startIndex: start, endIndex: end };
  }, []);

  // If nodes are rotable, compute side-centered anchors based on angle to ensure snapping after rotation
  const getNodeById = useCallback((nid) => (reactFlow?.getNodes?.() || []).find((n) => n.id === nid), [reactFlow, srcAngle, tgtAngle]);
  const calcAnchor = useCallback((node, role, handleId) => {
    if (!node) return null;
    const x = (node.positionAbsolute?.x ?? node.position?.x ?? 0);
    const y = (node.positionAbsolute?.y ?? node.position?.y ?? 0);
    const w = node.width || 0;
    const h = node.height || 0;
    const ang = typeof node?.data?.angle === 'number' ? node.data.angle : 0;
    const isRot = !!node?.data?.isRotable;
    // Special case: bus node with multiple handles -> compute by handle id
    if (node?.type === 'busNode') {
      const layout = getBusLayout(node, gridGapPx);
      const roleKey = role === 'source' ? 'source' : 'target';
      return getBusHandleAnchor(node, layout, roleKey, handleId);
    }
    // For non-bus nodes with default handles (no list), snap to side centers based on angle
    const list = Array.isArray(node?.data?.handles) ? node.data.handles : null;
    if (!list || list.length === 0) {
      const vertical = ang === 0;
      if (vertical) {
        return role === 'source' ? { x: x + w / 2, y: y + h } : { x: x + w / 2, y };
      }
      // horizontal (rotated 90deg)
      return role === 'source' ? { x: x + w, y: y + h / 2 } : { x, y: y + h / 2 };
    }
    // For non-bus nodes with explicit handles, compute anchor when we know the handle or there is only one
    try {
      const anchors = computeCustomHandleAnchors(node, gridGapPx);
      if (anchors && anchors.length > 0) {
        const match = anchors.find((a) => (a.role || '').toLowerCase() === (role || '').toLowerCase() && String(a.id) === String(handleId));
        if (match) return { x: match.x, y: match.y };
        const fallback = anchors.find((a) => (a.role || '').toLowerCase() === (role || '').toLowerCase());
        if (fallback) return { x: fallback.x, y: fallback.y };
        if (anchors.length === 1) return { x: anchors[0].x, y: anchors[0].y };
      }
      if (!handleId && list.filter(h => (h?.type || '').toLowerCase() === (role || '').toLowerCase()).length !== 1) return null;
      const hc = list.find((h) => String(h?.id) === String(handleId));
      const chosen = hc || list.find((h) => (h?.type || '').toLowerCase() === (role || '').toLowerCase());
      if (!chosen) return null;
      const mapPos = (pos) => {
        if (ang === 0) return pos;
        switch (pos) {
          case Position.Top: return Position.Left;
          case Position.Bottom: return Position.Right;
          case Position.Left: return Position.Top;
          case Position.Right: return Position.Bottom;
          default: return pos;
        }
      };
      const mapped = mapPos(chosen.position);
      const style = chosen?.style || {};
      const pickPercent = (val, total) => {
        if (typeof val === 'number') return Math.max(0, Math.min(1, val / total));
        const s = String(val || '').trim();
        if (s.endsWith('%')) {
          const p = parseFloat(s.slice(0, -1));
          if (!isNaN(p)) return Math.max(0, Math.min(1, p / 100));
        }
        return 0.5;
      };
      // If original was top/bottom, spacing used left%; when rotated to left/right use same fraction for top%
      const frac = (chosen.position === Position.Top || chosen.position === Position.Bottom)
        ? pickPercent(style.left, w)
        : pickPercent(style.top, h);
      switch (mapped) {
        case Position.Top: return { x: x + w * frac, y };
        case Position.Bottom: return { x: x + w * frac, y: y + h };
        case Position.Left: return { x, y: y + h * frac };
        case Position.Right: return { x: x + w, y: y + h * frac };
        default: return null;
      }
    } catch {}
    // Fallback to RF props
    return null;
  }, [gridGapPx]);

  const srcNode = getNodeById(source);
  const tgtNode = getNodeById(target);
  // Read live handle ids from RF edge state (renderer props may omit them)
  const rfEdge = useMemo(() => (reactFlow?.getEdges?.() || []).find((e) => e.id === id), [reactFlow, id]);
  const liveSourceHandle = rfEdge?.sourceHandle;
  const liveTargetHandle = rfEdge?.targetHandle;
  const sOv = calcAnchor(srcNode, 'source', liveSourceHandle);
  const tOv = calcAnchor(tgtNode, 'target', liveTargetHandle);
  const sx = sOv?.x ?? sourceX;
  const sy = sOv?.y ?? sourceY;
  const rawWaypoints = useMemo(
    () => (Array.isArray(data.waypoints) ? data.waypoints : []),
    [data.waypoints],
  );
  const hasManualRoute = Array.isArray(localPoints)
    ? localPoints.length > 0
    : rawWaypoints.length > 0;
  const isSlidingBusTarget = useMemo(() => {
    if (!tgtNode || tgtNode.type !== 'busNode') return false;
    if (hasManualRoute) return false;
    const layout = getBusLayout(tgtNode, gridGapPx);
    return layout.horizontal;
  }, [tgtNode, gridGapPx, hasManualRoute]);
  const slidingBusTargetAnchor = useMemo(() => {
    if (!isSlidingBusTarget || !tgtNode) return null;
    const layout = getBusLayout(tgtNode, gridGapPx);
    const nodeX = tgtNode.positionAbsolute?.x ?? tgtNode.position?.x ?? 0;
    const nodeY = tgtNode.positionAbsolute?.y ?? tgtNode.position?.y ?? 0;
    return {
      x: clampValue(sx, nodeX, nodeX + layout.length),
      y: nodeY,
    };
  }, [isSlidingBusTarget, tgtNode, sx, gridGapPx]);
  const tx = slidingBusTargetAnchor?.x ?? tOv?.x ?? targetX;
  const ty = slidingBusTargetAnchor?.y ?? tOv?.y ?? targetY;

  // Lead-out/in segments so lines don't run along the bus bar
  const sLead = useMemo(() => {
    if (!srcNode || srcNode.type !== 'busNode') return null;
    const layout = getBusLayout(srcNode, gridGapPx);
    const lead = Math.max(10, layout.thickness + 6);
    if (layout.horizontal) {
      return { x: sx, y: sy + lead };
    }
    return { x: sx + lead, y: sy };
  }, [srcNode, sx, sy, gridGapPx]);

  const tLead = useMemo(() => {
    if (!tgtNode || tgtNode.type !== 'busNode') return null;
    const layout = getBusLayout(tgtNode, gridGapPx);
    const lead = Math.max(10, layout.thickness + 6);
    if (layout.horizontal) {
      return { x: tx, y: ty - lead };
    }
    return { x: tx - lead, y: ty };
  }, [tgtNode, tx, ty, gridGapPx]);

  const waypoints = useMemo(
    () => (isSlidingBusTarget ? [] : rawWaypoints),
    [isSlidingBusTarget, rawWaypoints],
  );
  const combined = useMemo(() => {
    const arr = [];
    const usingManualRoute = Array.isArray(localPoints)
      ? localPoints.length > 0
      : Array.isArray(waypoints) && waypoints.length > 0;
    if (sLead && !usingManualRoute) arr.push(sLead);
    if (Array.isArray(localPoints)) arr.push(...localPoints);
    else if (Array.isArray(waypoints)) arr.push(...waypoints);
    if (tLead && !usingManualRoute) arr.push(tLead);
    return arr;
  }, [sLead, tLead, localPoints, waypoints]);
  

  const points = useMemo(() => buildPoints(sx, sy, tx, ty, combined), [sx, sy, tx, ty, combined, srcAngle, tgtAngle]);
  const renderPoints = useMemo(
    () => extendEndpointOverlap(
      points,
      getEndpointOverlapPx(srcAngle),
      getEndpointOverlapPx(tgtAngle),
    ),
    [points, srcAngle, tgtAngle],
  );

  const commitWaypoints = useCallback((pts) => {
    const inner = pts.slice(1, pts.length - 1);
    const merged = mergeCollinear(inner);
    dispatch(updateEdgeData({ edgeId: id, data: { waypoints: merged } }));
  }, [dispatch, id]);

  const deleteWaypoint = useCallback((pointIndex) => {
    // pointIndex corresponds to index in full points array
    const inner = points.slice(1, points.length - 1);
    const innerIdx = pointIndex - 1;
    if (innerIdx < 0 || innerIdx >= inner.length) return;
    const nextInner = [...inner];
    nextInner.splice(innerIdx, 1);
    const finalPts = buildPoints(sx, sy, tx, ty, nextInner);
    commitWaypoints(finalPts);
    setLocalPoints(null);
    setSelectedPoint(-1);
  }, [points, commitWaypoints, sx, sy, tx, ty]);

  const segmentHandles = useMemo(() => {
    if (!points || points.length < 2) return [];
    const list = [];
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i];
      const b = points[i + 1];
      if (!a || !b) continue;
      const isVertical = a.x === b.x;
      const isHorizontal = a.y === b.y;
      if (!isVertical && !isHorizontal) continue;
      const length = isVertical ? Math.abs(b.y - a.y) : Math.abs(b.x - a.x);
      if (length === 0) continue;
      const midX = isVertical ? a.x : (a.x + b.x) / 2;
      const midY = isHorizontal ? a.y : (a.y + b.y) / 2;
      const centerX = Math.round(midX * 100) / 100;
      const centerY = Math.round(midY * 100) / 100;
      list.push({
        index: i,
        x: centerX,
        y: centerY,
        isVertical,
      });
    }
    return list;
  }, [points]);

  // Split polyline into two parts by cutting a gap around the center position
  // Drag handlers for bend points
  useEffect(() => {
    if (dragIndex < 0) return;
    const handleMove = (evt) => {
      const svgRect = containerRef.current?.getBoundingClientRect();
      if (!svgRect) return;
      const mx = snap(evt.clientX - svgRect.left);
      const my = snap(evt.clientY - svgRect.top);
      // force orth corner between neighbors
      const prev = points[dragIndex - 1];
      const next = points[dragIndex + 1];
      const cand1 = { x: prev.x, y: my };
      const cand2 = { x: mx, y: next.y };
      const d1 = Math.hypot(cand1.x - mx, cand1.y - my);
      const d2 = Math.hypot(cand2.x - mx, cand2.y - my);
      const chosen = d1 < d2 ? cand1 : cand2;
      const updated = points.map((pt, idx) => (idx === dragIndex ? chosen : { ...pt }));
      const normalized = normalizeEndpointOffsets(updated);
      setLocalPoints(normalized.slice(1, normalized.length - 1));
    };
    const handleUp = () => {
      if (localPoints) {
        const finalPts = buildPoints(sx, sy, tx, ty, localPoints);
        commitWaypoints(finalPts);
      }
      setDragIndex(-1);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp, { once: true });
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragIndex, points, localPoints, commitWaypoints, sx, sy, tx, ty]);

  // Delete selected point with Delete/Backspace
  useEffect(() => {
    if (selectedPoint < 0) return;
    const onKey = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const inner = points.slice(1, points.length - 1);
        if (selectedPoint - 1 >= 0 && selectedPoint - 1 < inner.length) {
          const nextInner = [...inner];
          nextInner.splice(selectedPoint - 1, 1);
          const finalPts = buildPoints(sx, sy, tx, ty, nextInner);
          commitWaypoints(finalPts);
          setLocalPoints(null);
          setSelectedPoint(-1);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedPoint, points, commitWaypoints, sx, sy, tx, ty]);

  const selectedEdgeIds = useSelector((state) => state.edge.selectedEdgeIds) || [];
  const isSelected = selectedEdgeIds.includes(id);
  const baseStroke = (data && data.options && typeof data.options.stroke === 'string')
    ? data.options.stroke.trim()
    : null;
  const resolvedStroke = baseStroke || '#222';
  const highlightStroke = '#2cabef';
  const resizingBusNodeId = useSelector((state) => state.view.resizingBusNodeId);
  const isBusResizeConnection = !!resizingBusNodeId && (source === resizingBusNodeId || target === resizingBusNodeId);
  const stroke = isBusResizeConnection ? '#9ca3af' : (isSelected ? highlightStroke : resolvedStroke);
  const lineStrokePx = useSelector((state) => state.view.lineStrokePx) || 3;
  const strokeWidth = lineStrokePx;
  const straightTolerance = Math.max(8, strokeWidth * 2);
  const displayPoints = useMemo(
    () => straightenPolyline(renderPoints, straightTolerance),
    [renderPoints, straightTolerance],
  );
  const displayPath = useMemo(() => toPath(displayPoints), [displayPoints]);
  const displaySegmentHandles = useMemo(
    () => (isStraightishPolyline(renderPoints, straightTolerance) ? [] : segmentHandles),
    [renderPoints, segmentHandles, straightTolerance],
  );

  const startSegmentDrag = useCallback(
    (event, segIndex, isVerticalSegment) => {
      if (!containerRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      const rect = containerRef.current.getBoundingClientRect();
      const basePoints = points.map((pt) => ({ ...pt }));
      const orientation = isVerticalSegment ? 'vertical' : 'horizontal';

      if (basePoints.length <= 2) {
        const gridGap = Math.max(5, Math.min(200, Number(gridGapPx) || 25));
        const baseOffset = gridGap * 2;
        const first = basePoints[0];
        const last = basePoints[basePoints.length - 1] || first;
        if (orientation === 'vertical') {
          const span = Math.abs((last?.y ?? first.y) - first.y);
          let offset = Math.min(baseOffset, span / 2);
          if (!Number.isFinite(offset) || offset <= 0) offset = gridGap;
          const dir = (last?.y ?? first.y) >= first.y ? 1 : -1;
          const top = { x: first.x, y: first.y + (dir === 0 ? 1 : dir) * offset };
          const bottom = { x: last?.x ?? first.x, y: (last?.y ?? first.y) - (dir === 0 ? 1 : dir) * offset };
          basePoints.splice(1, 0, top);
          basePoints.splice(basePoints.length - 1, 0, bottom);
        } else {
          const span = Math.abs((last?.x ?? first.x) - first.x);
          let offset = Math.min(baseOffset, span / 2);
          if (!Number.isFinite(offset) || offset <= 0) offset = gridGap;
          const dir = (last?.x ?? first.x) >= first.x ? 1 : -1;
          const left = { x: first.x + (dir === 0 ? 1 : dir) * offset, y: first.y };
          const right = { x: (last?.x ?? first.x) - (dir === 0 ? 1 : dir) * offset, y: last?.y ?? first.y };
          basePoints.splice(1, 0, left);
          basePoints.splice(basePoints.length - 1, 0, right);
        }
        setLocalPoints(basePoints.slice(1, basePoints.length - 1));
      }

      const { startIndex, endIndex } = computeSegmentRange(segIndex, isVerticalSegment, basePoints);
      if (startIndex >= endIndex && basePoints.length > 2) {
        // allow moving single inner point as long as it exists
        if (startIndex === endIndex && (startIndex > 0 && startIndex < basePoints.length - 1)) {
          // ok
        } else {
          return;
        }
      }
      const initialSnap = orientation === 'vertical'
        ? snap(event.clientX - rect.left)
        : snap(event.clientY - rect.top);
      segmentDragRef.current = {
        orientation,
        basePoints,
        rect,
        startIndex,
        endIndex,
        initialSnap,
        lastDelta: 0,
        latestInner: basePoints.slice(1, basePoints.length - 1),
        segmentIndex: segIndex,
      };
      setSegmentDragging(true);
      setHoverPath(true);
      setActiveSegmentIndex(segIndex);
      setHoveredSegmentIndex(segIndex);
    },
    [points, computeSegmentRange, snap, gridGapPx]
  );

  useEffect(() => {
    if (!segmentDragging) return;
    const handleMove = (evt) => {
      const drag = segmentDragRef.current;
      if (!drag) return;
      const { orientation, rect, basePoints, startIndex, endIndex, initialSnap } = drag;
      const currentSnap = orientation === 'vertical'
        ? snap(evt.clientX - rect.left)
        : snap(evt.clientY - rect.top);
      const delta = currentSnap - initialSnap;
      if (delta === drag.lastDelta) {
        return;
      }
      drag.lastDelta = delta;
      const updated = basePoints.map((pt) => ({ ...pt }));
      if (orientation === 'vertical') {
        for (let i = startIndex; i <= endIndex; i += 1) {
          updated[i].x = basePoints[i].x + delta;
        }
      } else {
        for (let i = startIndex; i <= endIndex; i += 1) {
          updated[i].y = basePoints[i].y + delta;
        }
      }
      const normalized = normalizeEndpointOffsets(updated);
      const inner = normalized.slice(1, normalized.length - 1);
      drag.latestInner = inner;
      setLocalPoints(inner);
    };
    const handleUp = () => {
      const drag = segmentDragRef.current;
      if (drag && drag.latestInner) {
        const finalPts = buildPoints(sx, sy, tx, ty, drag.latestInner);
        commitWaypoints(finalPts);
      }
      segmentDragRef.current = null;
      setSegmentDragging(false);
      setLocalPoints(null);
      setActiveSegmentIndex(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp, { once: true });
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [segmentDragging, snap, commitWaypoints, sx, sy, tx, ty]);

  return (
    <>
      <BaseEdge id={id} path={displayPath} style={{ stroke, strokeWidth }} markerStart={markerStart} markerEnd={markerEnd} />
      <EdgeLabelRenderer>
        <div className="nodrag nopan" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <svg
            ref={containerRef}
            style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'auto' }}
          >
            <path
              d={displayPath}
              fill="none"
              stroke="transparent"
              strokeWidth={24}
              style={{ pointerEvents: 'none' }}
            />
            {displayPoints.slice(0, displayPoints.length - 1).map((start, idx) => {
              const end = displayPoints[idx + 1];
              if (!end) return null;
              const samePoint = start.x === end.x && start.y === end.y;
              if (samePoint) return null;
              return (
                <line
                  key={`segment-hover-${idx}`}
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                  stroke="transparent"
                  strokeWidth={28}
                  strokeLinecap="round"
                  style={{ pointerEvents: 'stroke' }}
                  onMouseEnter={() => {
                    setHoverPath(true);
                    setHoveredSegmentIndex(idx);
                  }}
                  onMouseLeave={(evt) => {
                    if (segmentDragging && activeSegmentIndex === idx) {
                      return;
                    }
                    const rel = evt.relatedTarget;
                    const isHandle = rel && typeof rel.closest === 'function' && rel.closest('.edge-move-handle');
                    const container = containerRef.current;
                    const staysOnSvg = !isHandle && container && rel instanceof SVGElement && container.contains(rel);
                    if (isHandle || staysOnSvg) {
                      return;
                    }
                    setHoverPath(false);
                    if (!segmentDragging) {
                      setHoveredSegmentIndex(null);
                    }
                  }}
                />
              );
            })}
          </svg>
          {displayPoints.slice(1, displayPoints.length - 1).map((pt, idx) => {
            const pointIndex = idx + 1;
            if (isCollinearPoint(displayPoints[pointIndex - 1], pt, displayPoints[pointIndex + 1])) {
              return null;
            }
            return (
              <div
                key={`bend-${pointIndex}`}
                className="ux-only edge-bend-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDragIndex(pointIndex);
                  setSelectedPoint(pointIndex);
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteWaypoint(pointIndex);
                }}
                style={{
                  position: 'absolute',
                  transform: `translate(${pt.x}px, ${pt.y}px) translate(-6px, -6px)`,
                  width: 12,
                  height: 12,
                  background: '#fff',
                  border: '2px solid #2cabef',
                  borderRadius: 2,
                  cursor: 'move',
                  pointerEvents: 'auto',
                }}
              />
            );
          })}
          {displaySegmentHandles.map(({ index, x, y, isVertical }) => {
            const isActive = segmentDragging && activeSegmentIndex === index;
            const shouldShow = isActive || (!segmentDragging && hoveredSegmentIndex === index);
            if (!shouldShow) return null;
            return (
              <div
                key={`segment-handle-${index}`}
                className={`edge-move-handle${isActive ? ' is-dragging' : ''}`}
                onMouseDown={(event) => startSegmentDrag(event, index, isVertical)}
                onMouseEnter={() => {
                  setHoverPath(true);
                  setHoveredSegmentIndex(index);
                }}
                onMouseLeave={(evt) => {
                  if (segmentDragging && activeSegmentIndex === index) {
                    return;
                  }
                  const rel = evt.relatedTarget;
                  const container = containerRef.current;
                  const isHandle = rel && typeof rel.closest === 'function' && rel.closest('.edge-move-handle');
                  const staysOnSvg = !isHandle && container && rel instanceof SVGElement && container.contains(rel);
                  if (isHandle || staysOnSvg) {
                    return;
                  }
                  setHoverPath(false);
                  if (!segmentDragging) {
                    setHoveredSegmentIndex(null);
                  }
                }}
                style={{
                  transform: `translate3d(${x}px, ${y}px, 0) translate(-14px, -14px)`,
                }}
              >
                <MoveHandleIcon direction={isVertical ? 'horizontal' : 'vertical'} />
              </div>
            );
          })}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default OrthogonalEdge;
